import Dropdown from 'antd/es/dropdown';
import cx from 'clsx';
import React, { forwardRef, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { IconRcClear } from 'assets';
import { HelperTooltip } from 'components';

import { findLastIndex } from 'lodash';
import { SearchInput } from '../Input';
import './index.css';
import { NotFoundContent, SearchSelectOptionPanel } from './Option';
import { SearchSelectProps, SelectOption } from './types';
import { flattenArr, scrollIntoView } from './utils';

export const SearchSelect = memo(
  forwardRef(
    (
      {
        autoClearSearchValue = true,
        options = [],
        className,
        panelClassName,
        inputClassName,
        inputActiveClassName,
        onSearch,
        onSelect,
        onFocus,
        onBlur,
        placeholder,
        open,
        notFoundContent = <NotFoundContent />,
        renderOption,
        formatSelectLabel,
        allowClear,
        prefix,
        initialValue,
        value,
        onChange,
        inParent = true,
        disabled,
        toolTip,
        onPressEnter,
        setOptions,
        preventBlur,
        setPreventBlur,
        panelStyle,
        searchValue,
        onSearchValueChange,
        iconAtEnd,
      }: SearchSelectProps,
      ref: any,
    ) => {
      const inputRef = useRef<HTMLInputElement>(null);
      const panelRef = useRef<HTMLDivElement>(null);
      const isControlled = useMemo(() => typeof open !== 'undefined', [open]);
      const isValueControlled = useMemo(() => typeof value !== 'undefined', [value]);
      const _initalValue = useMemo(() => {
        const _value = initialValue || value;
        if (!_value) return;
        return formatSelectLabel ? formatSelectLabel(_value) : _value.label;
      }, [initialValue, value, formatSelectLabel]);

      const [inputView, setInputView] = useState<string | undefined>(_initalValue);
      // const [inputModal, setInputModal] = useState<string | undefined>();
      const [inputFocus, setInputFocus] = useState(false);

      const [activeIdx, setActiveIdx] = useState<number>(-1);
      const [dropdownShow, setDropdownShow] = useState<boolean>(open || false);
      // const [optionsModal, setOptions] = useState<SelectOption[]>(options);
      const optionsModal = options;
      const flattenOptions = useMemo(() => flattenArr(optionsModal), [optionsModal]);
      const isCompositionUpdateRef = useRef(false);

      const _setDropdownShow = useCallback(
        (show: boolean | undefined) => {
          if (show !== dropdownShow && !isControlled) {
            setDropdownShow(show!);
          }
        },
        [dropdownShow, isControlled],
      );

      const _setInputValue = useCallback(
        (val: SelectOption) => {
          if (!isValueControlled) {
            const _input = autoClearSearchValue
              ? ''
              : formatSelectLabel
                ? formatSelectLabel(val)
                : val.label;
            setInputView(_input);
          }

          onSearchValueChange?.('');
          onChange?.(val);
        },
        [isValueControlled, onSearchValueChange, onChange, autoClearSearchValue, formatSelectLabel],
      );

      useEffect(() => {
        const _input = !value ? '' : formatSelectLabel ? formatSelectLabel(value) : value.label;
        setInputView(_input);
      }, [value, setInputView, formatSelectLabel]);

      useEffect(() => {
        const handleCompositionStart = () => {
          isCompositionUpdateRef.current = true;
        };
        const handleCompositionEnd = () => {
          isCompositionUpdateRef.current = false;
        };
        const current = inputRef.current;
        current?.addEventListener('compositionstart', handleCompositionStart);
        current?.addEventListener('compositionend', handleCompositionEnd);

        return () => {
          current?.removeEventListener('compositionstart', handleCompositionStart);
          current?.removeEventListener('compositionend', handleCompositionEnd);
        };
      }, []);

      useEffect(() => {
        setActiveIdx(-1);
      }, [options]);

      useEffect(() => {
        if (!dropdownShow) {
          setActiveIdx(-1);
        }
      }, [dropdownShow]);

      useEffect(() => {
        if (panelRef?.current) {
          const target: HTMLElement | null = panelRef.current.querySelector(
            '.searchSelect-option--active',
          );
          if (target) {
            scrollIntoView(panelRef?.current, target);
          }
        }
      }, [activeIdx]);

      useEffect(() => {
        if (isControlled) {
          setDropdownShow(open!);
        }
      }, [isControlled, open, _setDropdownShow]);

      const handleSelect = useCallback(
        (idx: number) => {
          // preventBlur.current = true;
          const val = flattenOptions[idx];
          setActiveIdx(idx);
          _setInputValue(val);
          setInputFocus(false);
          inputRef.current!.blur();
          _setDropdownShow(false);
          onSelect?.(val);
        },
        [onSelect, _setInputValue, setActiveIdx, flattenOptions, _setDropdownShow],
      );

      const handleFocus = useCallback(
        e => {
          onFocus?.(e);
          setInputFocus(true);
          if (inputView && optionsModal?.length) {
            _setDropdownShow(true);
          }
        },
        [onFocus, inputView, optionsModal?.length, _setDropdownShow],
      );

      const handleBlur = useCallback(
        (e: React.FocusEvent<HTMLInputElement>) => {
          if (preventBlur) {
            return;
          }
          setInputFocus(false);
          _setDropdownShow(false);
          onSearchValueChange?.('');
          onBlur?.(e);

          if (activeIdx === -1) {
            setOptions?.([]);
          }
        },
        [preventBlur, _setDropdownShow, onSearchValueChange, onBlur, activeIdx, setOptions],
      );

      const handleInputChange = useCallback(
        (value?) => {
          onSearchValueChange?.(value);
          if (typeof value === 'undefined') {
            onChange?.(value);
          }
          onSearch?.(value);
          // _setDropdownShow(value ? true : false);
          if (!value) {
            setOptions?.([]);
          }
        },
        [onSearchValueChange, onSearch, onChange, setOptions],
      );

      const handleClear = useCallback(() => {
        handleInputChange();
      }, [handleInputChange]);

      const handleOptionClick = useCallback(
        idx => {
          handleSelect(idx);
          _setDropdownShow(false);
        },
        [handleSelect, _setDropdownShow],
      );

      const handleKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement> | KeyboardEvent) => {
          if (
            !flattenOptions?.length ||
            !dropdownShow ||
            isCompositionUpdateRef.current ||
            document.querySelector('.memo-edit-modal')
          ) {
            return;
          }
          const realIdx = activeIdx === -1 ? 0 : activeIdx;
          switch (e.key) {
            case 'ArrowUp':
              e.preventDefault();
              const preIndex = findLastIndex(flattenOptions, (item, index) => {
                return index < realIdx && !item.isHideEnter;
              });
              if (preIndex !== -1) {
                setActiveIdx(preIndex);
              }
              break;
            case 'ArrowDown':
              e.preventDefault();
              const nextIndex = flattenOptions.findIndex((item, index) => {
                return index > realIdx && !item.isHideEnter;
              });
              if (nextIndex !== -1) {
                setActiveIdx(nextIndex);
              }
              break;
            case 'Enter':
              e.preventDefault();
              handleSelect(realIdx);
              break;
            default:
          }
        },
        [flattenOptions, dropdownShow, activeIdx, handleSelect],
      );

      useEffect(() => {
        setInputFocus(dropdownShow);
      }, [dropdownShow]);

      return (
        <div className={cx('searchSelect', className)} ref={ref}>
          <Dropdown
            trigger={['click']}
            disabled={disabled}
            visible={dropdownShow}
            getPopupContainer={inParent ? dom => dom : undefined}
            overlay={
              <div
                ref={panelRef}
                style={panelStyle}
                className={cx('searchSelect-optionPanel', panelClassName)}
                onMouseDown={e => {
                  setPreventBlur?.(true);
                  setTimeout(() => {
                    setPreventBlur?.(false);
                  }, 500);
                }}
                onClick={() => {
                  // setPreventBlur?.(true);
                }}>
                {flattenOptions?.length || optionsModal.length ? (
                  <SearchSelectOptionPanel
                    options={optionsModal}
                    activeIdx={
                      activeIdx === -1
                        ? flattenOptions.findIndex((item, index) => {
                            return !item.isHideEnter;
                          })
                        : activeIdx
                    }
                    renderOption={renderOption}
                    onOptionClick={handleOptionClick}
                  />
                ) : (
                  notFoundContent
                )}
              </div>
            }>
            <HelperTooltip title={toolTip}>
              <div
                className={cx(
                  'searchSelect-inputWrap',
                  inputClassName,
                  disabled && 'searchSelect-inputWrapDisabled',
                  inputFocus && (inputActiveClassName || 'searchSelect-v2-inputWrapFocus'),
                )}>
                {!searchValue && (
                  <span
                    className={cx(
                      'searchSelect-mask',
                      (!inputView || inputFocus) && 'empty',
                      (prefix || typeof prefix === 'undefined') &&
                        !iconAtEnd &&
                        'searchSelect-mask--prefix',
                    )}>
                    {inputView || placeholder}
                  </span>
                )}
                <SearchInput
                  className={cx('searchSelect-input', allowClear && 'is-clearable')}
                  activeClassName={false}
                  value={searchValue}
                  onFocus={handleFocus}
                  onBlur={handleBlur}
                  onKeyDown={handleKeyDown}
                  onChange={handleInputChange}
                  prefix={prefix}
                  ref={inputRef}
                  disabled={disabled}
                  iconAtEnd={iconAtEnd}
                />
                {allowClear && (inputView || searchValue) && (
                  <IconRcClear className="searchInput-clearIcon" onClick={handleClear} />
                )}
              </div>
            </HelperTooltip>
          </Dropdown>
        </div>
      );
    },
  ),
);

export default SearchSelect;
export * from './types';
